import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { usePagination } from '../../../shared/hooks/usePagination';
import { tapHaptic } from '../../../shared/lib/haptics';
import { Button } from '../../../shared/ui/Button';
import { FormError } from '../../../shared/ui/FormError';
import { Input } from '../../../shared/ui/Input';
import { Label } from '../../../shared/ui/Label';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { Pagination } from '../../../shared/ui/Pagination';
import { SearchInput } from '../../../shared/ui/SearchInput';
import { Select } from '../../../shared/ui/Select';
import { Skeleton } from '../../../shared/ui/Skeleton';
import { SkillFilter, type SkillFilterOption } from '../../../shared/ui/SkillFilter';
import { StatusBanner } from '../../../shared/ui/StatusBanner';
import { useToast } from '../../../shared/ui/toast-context';
import {
  createGlobalTest,
  listGlobalTests,
  QBANK_QUERY_KEYS,
  type ExamType,
  type GlobalTestListRow,
  type SectionKind,
  type TestRow,
} from '../tests-api';

import s from './TestsListPage.module.css';

function statusLabel(status: TestRow['status']): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'published':
      return 'Published';
    case 'archived':
      return 'Archived';
  }
}

function statusBadgeClass(status: TestRow['status']): string {
  switch (status) {
    case 'draft':
      return s.statusDraft ?? '';
    case 'published':
      return s.statusPublished ?? '';
    case 'archived':
      return s.statusArchived ?? '';
  }
}

const SECTION_LABEL = new Map<SectionKind, string>([
  ['listening', 'Listening'],
  ['reading', 'Reading'],
  ['writing', 'Writing'],
  ['speaking', 'Speaking'],
]);
const SECTION_ORDER: SectionKind[] = ['listening', 'reading', 'writing', 'speaking'];
const SKILL_FILTER_OPTIONS: SkillFilterOption<SectionKind>[] = SECTION_ORDER.map((kind) => ({
  value: kind,
  label: SECTION_LABEL.get(kind) ?? kind,
}));

const createTestSchema = z.object({
  title: z.string().trim().min(3, 'Title is required').max(160),
  exam_type: z.enum(['academic', 'general']),
  total_duration_minutes: z
    .string()
    .trim()
    .refine((v) => {
      const n = Number.parseInt(v, 10);
      return !Number.isNaN(n) && n >= 30 && n <= 240;
    }, 'Duration must be between 30 and 240 minutes'),
});
type CreateTestInput = z.infer<typeof createTestSchema>;

const SKELETON_ROWS = [0, 1, 2, 3, 4] as const;

/** Mirrors `.row` — title, meta line, section chips, status badge, Edit link. */
function TestRowSkeleton(): JSX.Element {
  return (
    <li className={s.row}>
      <div className={s.rowMain}>
        <Skeleton width="60%" height={16} />
        <Skeleton width="35%" height={14} />
        <Skeleton width={72} height={16} radius="xl" />
      </div>
      <Skeleton width={80} height={22} radius="xl" className={s.badge ?? ''} />
      <Skeleton width={48} height={32} radius="sm" className={s.manage ?? ''} />
    </li>
  );
}

export function AdminTestsListPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [serverError, setServerError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [skills, setSkills] = useState<ReadonlySet<SectionKind>>(new Set());

  const list = useQuery({
    queryKey: QBANK_QUERY_KEYS.tests,
    queryFn: listGlobalTests,
  });

  const create = useMutation({
    mutationFn: createGlobalTest,
    onSuccess: (row: TestRow) => {
      toast.show(`Created "${row.title}". Add sections to start building.`, {
        tone: 'success',
      });
      tapHaptic();
      reset();
      void queryClient.invalidateQueries({ queryKey: QBANK_QUERY_KEYS.tests });
    },
    onError: (err: Error) => setServerError(err.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateTestInput>({
    resolver: zodResolver(createTestSchema),
    defaultValues: { title: '', exam_type: 'academic', total_duration_minutes: '165' },
  });

  async function onSubmit(values: CreateTestInput): Promise<void> {
    setServerError(null);
    try {
      await create.mutateAsync({
        title: values.title,
        description: null,
        exam_type: values.exam_type as ExamType,
        total_duration_minutes: Number.parseInt(values.total_duration_minutes, 10),
      });
    } catch {
      /* onError set the message */
    }
  }

  function toggleSkill(kind: SectionKind): void {
    setSkills((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  const allRows = useMemo<GlobalTestListRow[]>(() => list.data ?? [], [list.data]);

  const filteredRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return allRows.filter((row) => {
      const titleMatch = needle === '' || row.title.toLowerCase().includes(needle);
      const skillMatch = skills.size === 0 || row.section_kinds.some((k) => skills.has(k));
      return titleMatch && skillMatch;
    });
  }, [allRows, query, skills]);

  const pages = usePagination(filteredRows);
  const filtering = query.trim() !== '' || skills.size > 0;

  return (
    <div className={s.root}>
      <PageHeader
        eyebrow="Question bank"
        title="Global tests"
        subtitle={
          <>
            Build official IELTS mock tests that every institute on the platform can schedule. New
            tests start as <strong>Draft</strong>; flip to <strong>Published</strong> once sections
            and answer keys are complete.
          </>
        }
      />

      <section className={s.section}>
        <div className={s.sectionHead}>
          <h2 className={s.sectionTitle}>Create a global test</h2>
        </div>
        <form
          noValidate
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
          className={s.form}
        >
          <div className={s.formMain}>
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              autoComplete="off"
              placeholder="Cambridge IELTS Academic 19 — Test 1"
              invalid={errors.title !== undefined}
              aria-describedby={errors.title ? 'title-error' : undefined}
              {...register('title')}
            />
            <FormError id="title-error" message={errors.title?.message} />
          </div>
          <div>
            <Label htmlFor="exam_type">Exam type</Label>
            <Select id="exam_type" {...register('exam_type')}>
              <option value="academic">Academic</option>
              <option value="general">General Training</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="total_duration_minutes">Total duration (min)</Label>
            <Input
              id="total_duration_minutes"
              type="number"
              inputMode="numeric"
              min={30}
              max={240}
              invalid={errors.total_duration_minutes !== undefined}
              aria-describedby={errors.total_duration_minutes ? 'duration-error' : undefined}
              {...register('total_duration_minutes')}
            />
            <FormError id="duration-error" message={errors.total_duration_minutes?.message} />
          </div>

          {serverError !== null ? (
            <div className={s.formFull}>
              <StatusBanner tone="error">{serverError}</StatusBanner>
            </div>
          ) : null}

          <div className={`${s.formFull} ${s.formActions}`}>
            <Button type="submit" loading={isSubmitting || create.isPending}>
              Create test
            </Button>
          </div>
        </form>
      </section>

      <section className={s.section}>
        <div className={s.sectionHead}>
          <h2 className={s.sectionTitle}>All global tests</h2>
          <span className={s.sectionMeta}>
            {filtering
              ? `${String(filteredRows.length)} of ${String(allRows.length)}`
              : allRows.length}{' '}
            total
          </span>
        </div>

        {allRows.length > 0 ? (
          <div className={s.listControls}>
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder="Search tests…"
              label="Search global tests by title"
            />
            <SkillFilter
              options={SKILL_FILTER_OPTIONS}
              selected={skills}
              onToggle={toggleSkill}
              label="Filter global tests by skill"
            />
          </div>
        ) : null}

        {list.isPending ? (
          <ul className={s.list} role="status" aria-label="Loading global tests">
            {SKELETON_ROWS.map((row) => (
              <TestRowSkeleton key={row} />
            ))}
          </ul>
        ) : list.error !== null ? (
          <StatusBanner tone="error">Could not load tests: {list.error.message}</StatusBanner>
        ) : allRows.length === 0 ? (
          <p className={s.empty}>No global tests yet. Create one above to get started.</p>
        ) : filteredRows.length === 0 ? (
          <p className={s.empty}>
            No tests match {query.trim() !== '' ? `“${query.trim()}”` : 'the selected skills'}. Try
            clearing the search or a filter.
          </p>
        ) : (
          <>
            <ul className={s.list}>
              {pages.pageItems.map((row) => (
                <li key={row.id} className={s.row}>
                  <div className={s.rowMain}>
                    <span className={s.rowName}>{row.title}</span>
                    <span className={s.rowMeta}>
                      {row.exam_type === 'academic' ? 'Academic' : 'General Training'} ·{' '}
                      {row.total_duration_minutes} min
                    </span>
                    {row.section_kinds.length > 0 ? (
                      <ul className={s.kinds} aria-label="Sections">
                        {SECTION_ORDER.filter((k) => row.section_kinds.includes(k)).map((k) => (
                          <li key={k} className={s.kindChip}>
                            {SECTION_LABEL.get(k) ?? k}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                  <span className={`${s.badge} ${statusBadgeClass(row.status)}`}>
                    {statusLabel(row.status)}
                  </span>
                  <Link to={`/admin/tests/${row.id}`} className={s.manage}>
                    Edit
                  </Link>
                </li>
              ))}
            </ul>
            <Pagination
              page={pages.page}
              pageCount={pages.pageCount}
              onPageChange={pages.setPage}
              label="Global test pages"
              summary={`${String(pages.rangeStart)}–${String(pages.rangeEnd)} of ${String(pages.total)} tests`}
            />
          </>
        )}
      </section>
    </div>
  );
}
